import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Tables } from '@/integrations/supabase/types';

/**
 * Since we are referencing accounts here, we pick only the columns queried
 * Add any other to the account object when new columns are referenced
 */
export type RentPayment = Tables<'rent_payments'> & {
  'account': Pick<Tables<"accounts">, 'name' | 'nickname'>;
};

export interface CreateRentPaymentInput {
  apartment_id: string;
  account_id: string;
  amount: number;
  payment_date: string;
  description?: string;
}

/**
 * Records a rent payment and credits the receiving account.
 * The income transaction and balance update go through create_transaction_with_balance so the
 * balance change is atomic and server-validated, and the transaction uses the account's currency.
 */
export async function recordRentPayment(input: CreateRentPaymentInput) {
  const { data: { user }, error: authError } = await supabase.auth.getUser();
  if (authError || !user) throw new Error('User not authenticated');

  const { data: account, error: accountError } = await supabase
    .from('accounts')
    .select('currency')
    .eq('id', input.account_id)
    .single();
  if (accountError || !account) throw accountError ?? new Error('Account not found');

  // 1. Insert rent payment record
  const { data: rentPayment, error: rentError } = await supabase
    .from('rent_payments')
    .insert({
      apartment_id: input.apartment_id,
      user_id: user.id,
      account_id: input.account_id,
      amount: input.amount,
      payment_date: input.payment_date,
      description: input.description ?? null,
    })
    .select()
    .single();
  if (rentError) throw rentError;

  // 2. Insert income transaction and credit the account balance
  const { error: txError } = await supabase.rpc('create_transaction_with_balance', {
    p_user_id: user.id,
    p_account_id: input.account_id,
    p_amount: input.amount,
    p_type: 'income',
    p_transaction_date: input.payment_date,
    p_currency: account.currency,
    p_description: input.description ?? null,
  });
  if (txError) throw txError;

  return rentPayment;
}

export function useRentPayments(apartmentId: string | undefined) {
  const qc = useQueryClient();

  /**
   * This queries the accounts as well to get the accounts the rent payments are being submitted t
   */
  const query = useQuery({
    queryKey: ['rent-payments', apartmentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rent_payments')
        .select(`
          *, 
          account:accounts(
            name,
            nickname
          )
        `)
        .eq('apartment_id', apartmentId!)
        .order('payment_date', { ascending: false });
      if (error) throw error;
      return data as RentPayment[];
    },
    enabled: !!apartmentId,
  });

  const createRentPayment = useMutation({
    mutationFn: recordRentPayment,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rent-payments', apartmentId] });
      qc.invalidateQueries({ queryKey: ['upcoming-rent-schedule', apartmentId] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      toast.success('Rent payment recorded successfully');
    },
  });

  return { ...query, createRentPayment };
}
